/**
 * ADORACARE-VISUAL-SYSTEM-V1 -- THE REFERENCE SCREEN: My Work (call-centre
 * agent lens), rendered on the reference visual system. Mounted by
 * CallCenterWorkspacePage ONLY when ui/visual-variant.ts resolves 'v1'
 * (`?visual=v1` on this route) AND the server-decided lens is 'agent';
 * without the switch, or on the manager lens, the page renders exactly as
 * before. Nothing here fetches, decides authority, or writes: the page
 * hands this component the SAME view, the SAME filter codec, the SAME
 * selection identity and the SAME audience predicates it already computes
 * for the current render (ONE truth, two presentations).
 *
 * MY-WORK-PRIMITIVE-CONFORMANCE-V6 (founder ruling 2026-09-03: "the
 * prototype is the reference; you implement it"): this screen now COMPOSES
 * the conformed primitives and invents nothing --
 *   - PageHeader ............ the title and the decision line (.ac-statline)
 *   - MasterDetail / Master . the queue card: SegmentedControl (the primary
 *                             filter) + QuickFilters in the head, the
 *                             register scrolling INSIDE the body, the count
 *                             and freshness in the 36px foot with Refresh
 *                             as a link-styled act (AcLink)
 *   - DataTable ............. ONE 40px line per row, ellipsis never wrap:
 *                             the name an ink AcLink to the full record
 *                             (the audience the "Open full view" door already
 *                             has), the clinic beside it, the work reason a
 *                             StatusChip whose tone comes from status-canon
 *                             (work_reason), the waiting figure in semantic
 *                             ink, the last activity muted, the next action
 *   - DetailAside ........... the selected lead's context: LeadQuickViewHost
 *                             (panel frame) renders the Detail card -- WORK
 *                             region over REFERENCE regions and the timeline
 * Same information, same workflow, same switch. The morning-priority
 * explainer, which V3 printed under the row's chip, MOVED off the row (no
 * row wraps, ever) onto the chip's title and into the detail as a queue
 * note, so nothing the operator could read is lost.
 *
 * Presentation only. No dialer, no WhatsApp send (charter binding law 3);
 * no strategy string renders (law 5); no contact value renders here, ever.
 */

import { useRef, type KeyboardEvent, type ReactNode } from 'react';

import type {
  CallcenterWorkspaceItemView,
  CallcenterWorkspaceView,
} from '@/dashboard/api';
import LeadQuickViewHost from '@/dashboard/leads/LeadQuickViewHost';
import {
  AcButton,
  AcLink,
  DataTable,
  Detail,
  DetailAside,
  EmptyState,
  Master,
  MasterDetail,
  NEEDS_YOU_ALL_CLEAR,
  PageHeader,
  QuickFilter,
  QuickFilters,
  ReferenceRegion,
  SegmentedControl,
  StatusChip,
  type DataTableColumn,
} from '@/dashboard/ui';
import { formatIstDateTime, formatIstShortDate, formatIstTime } from '@/lib/datetime-ist';
import { formatClinicLocalDateTime } from '@/lib/age';
import {
  morningPriorityExplainer,
  needsYouFigures,
  nextQueueRowIndex,
  ownerCellLabel,
  queueFilters,
  queueSummaryLine,
  waitingState,
  type CallCenterWorkspaceFilterState,
} from '@/dashboard/callcenter/callcenter-workspace-view';

export interface MyWorkV1Props {
  readonly view: CallcenterWorkspaceView;
  readonly filters: CallCenterWorkspaceFilterState;
  readonly writeFilters: (next: CallCenterWorkspaceFilterState) => void;
  /** The server-ordered queue after the page's own display filters. */
  readonly visibleItems: readonly CallcenterWorkspaceItemView[];
  /** ONE row identity (the page's), shared with the marker and the panel. */
  readonly rowKeyOf: (item: CallcenterWorkspaceItemView) => string;
  /** The current row (the operator's pick, else the queue's top lead). */
  readonly currentRowKey: string | null;
  /** The row door: marks the row; the panel follows. Never opens a modal. */
  readonly selectRow: (item: CallcenterWorkspaceItemView) => void;
  /** The SAME predicate the current render's "Open lead" carries. */
  readonly canSelectRow: (item: CallcenterWorkspaceItemView) => boolean;
  /** TRUE when the caller holds the panel's backing read. */
  readonly quickViewLawful: boolean;
  /** The full-record href for a row, or null when the audience may not go
   *  there (the SAME predicate the detail's "Open full view" door reads);
   *  null renders the name as plain text -- never a dead link. */
  readonly leadHrefOf?: (item: CallcenterWorkspaceItemView) => string | null;
  readonly onRefresh: () => void;
  /** The lead was worked in the panel: the page refetches and re-derives. */
  readonly onLeadChanged: () => void;
  /** The page-level door (Log phone enquiry), already audience-gated. */
  readonly phoneInDoor?: ReactNode;
  readonly rowDisplayName: (item: CallcenterWorkspaceItemView) => string;
}

const MIDDLE_DOT = '\u00b7';

/** The WAITING cell: the figure in its semantic ink (tabular); the word
 *  stays for screen readers so urgency is never colour alone. */
function WaitingCell({ item }: { readonly item: CallcenterWorkspaceItemView }): JSX.Element {
  const state = waitingState(item);
  return (
    <span
      className={`ac-wait ac-wait--${state.tone}`}
      data-testid="workspace-row-due"
      data-due-urgency={item.urgency}
    >
      {state.figure !== '' ? (
        <>
          <span className="ac-sr-only">{`${state.word} `}</span>
          {state.figure}
        </>
      ) : (
        state.word
      )}
    </span>
  );
}

/** The LAST ACTIVITY cell: the label and the short IST stamp on one line;
 *  the full stamp rides the title. */
function LastActivityCell({ item }: { readonly item: CallcenterWorkspaceItemView }): JSX.Element {
  if (item.lastActivity === null) {
    return (
      <span className="ac-muted" data-testid="workspace-row-activity">
        No activity recorded
      </span>
    );
  }
  const at = item.lastActivity.atIso;
  return (
    <span
      className="ac-muted"
      data-testid="workspace-row-activity"
      title={at !== null ? formatIstDateTime(at) : undefined}
    >
      {item.lastActivity.label}
      {at !== null ? ` ${MIDDLE_DOT} ${formatIstShortDate(at)}, ${formatIstTime(at)}` : ''}
    </span>
  );
}

export default function MyWorkV1({
  view,
  filters,
  writeFilters,
  visibleItems,
  rowKeyOf,
  currentRowKey,
  selectRow,
  canSelectRow,
  quickViewLawful,
  leadHrefOf,
  onRefresh,
  onLeadChanged,
  phoneInDoor,
  rowDisplayName,
}: MyWorkV1Props): JSX.Element {
  const queueRef = useRef<HTMLElement>(null);
  const figures = needsYouFigures(view);
  const toggles = queueFilters(view, filters);
  const stateToggles = toggles.filter((t) => t.group === 'state');
  const reasonToggles = toggles.filter((t) => t.group === 'reason');
  const selectedItem =
    currentRowKey === null
      ? null
      : visibleItems.find((it) => rowKeyOf(it) === currentRowKey) ?? null;
  // The detail renders ONLY where a row can be selected into it (the same
  // audience the door has): no read, no detail -- never a dead plane.
  const showPanel = quickViewLawful && view.items.length > 0;

  // KEYBOARD OPERATION: arrow keys walk the queue and move the selection;
  // Enter is DataTable's own row activation. Pure decision in
  // nextQueueRowIndex; the DOM walk here only finds and focuses the row.
  const onQueueKeyDown = (e: KeyboardEvent<HTMLElement>): void => {
    const root = queueRef.current;
    if (root === null) return;
    const target = e.target as HTMLElement;
    const row = target.closest('tbody tr');
    if (row === null) return;
    const rows = Array.from(root.querySelectorAll<HTMLTableRowElement>('tbody tr'));
    const idx = rows.indexOf(row as HTMLTableRowElement);
    const nextIdx = nextQueueRowIndex(e.key, idx, rows.length);
    if (nextIdx === null) return;
    e.preventDefault();
    rows[nextIdx]?.focus();
    const item = visibleItems[nextIdx];
    if (item !== undefined && canSelectRow(item)) selectRow(item);
  };

  const columns: DataTableColumn<CallcenterWorkspaceItemView>[] = [
    {
      key: 'who',
      header: 'Person',
      render: (item) => {
        const href = leadHrefOf?.(item) ?? null;
        const name = rowDisplayName(item);
        return (
          <span className="ac-who">
            {href !== null ? (
              <AcLink to={href} tone="ink" className="ac-who__name" testId="workspace-row-name">
                {name}
              </AcLink>
            ) : (
              <span className="ac-who__name" data-testid="workspace-row-name">
                {name}
              </span>
            )}
            {item.clinicLabel !== null ? (
              <span className="ac-who__meta">{item.clinicLabel}</span>
            ) : null}
            {/* OWNER: only when it is NOT you -- on MY work, "Assigned to you"
                on every row is a column that restates the page title. */}
            {item.owner.kind !== 'you' ? (
              <span className="ac-who__meta" data-testid="workspace-row-owner">
                <span className="ac-sr-only">Owner </span>
                {ownerCellLabel(item.owner)}
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      key: 'why',
      header: 'Why now',
      render: (item) => (
        // The CATEGORY chip: its tone is the canon's (status-canon
        // work_reason), its label the wire's; colour AND a glyph.
        <StatusChip
          domain="work_reason"
          status={item.workReason}
          label={item.workReasonLabel}
          title={item.morningPriority ? morningPriorityExplainer(view) : undefined}
          testId={`workspace-reason-${item.workReason}`}
        />
      ),
    },
    {
      key: 'waiting',
      header: 'Waiting',
      align: 'end',
      render: (item) => <WaitingCell item={item} />,
    },
    {
      key: 'activity',
      header: 'Last activity',
      render: (item) => <LastActivityCell item={item} />,
    },
    {
      key: 'next',
      header: 'Next',
      render: (item) => (
        <span className="ac-next" data-testid="workspace-row-next">
          {item.nextActionLabel}
        </span>
      ),
    },
  ];

  const filtersHead = (
    <>
      <SegmentedControl
        label="Queue filter"
        testId="workspace-filters-state"
        options={stateToggles.map((f) => ({
          key: f.key,
          label: f.label,
          count: f.count,
          pressed: f.active,
          testId: `workspace-filter-${f.key}-toggle`,
        }))}
        onSelect={(key) => {
          const f = stateToggles.find((t) => t.key === key);
          if (f !== undefined) writeFilters(f.next);
        }}
      />
      {reasonToggles.length > 0 ? (
        <QuickFilters label="Work reasons" testId="workspace-filters-reason">
          {reasonToggles.map((f) => (
            <QuickFilter
              key={f.key}
              label={f.label}
              count={f.count}
              pressed={f.active}
              onClick={() => writeFilters(f.next)}
              testId={`workspace-filter-${f.key}-toggle`}
            />
          ))}
        </QuickFilters>
      ) : null}
    </>
  );

  const foot = (
    <>
      {/* The count is a second "nothing" beside the all-clear on an empty
          queue; it says something only once there is a queue. */}
      {view.items.length > 0 ? (
        <>
          <span data-testid="workspace-queue-summary">
            {queueSummaryLine(visibleItems.length, view.items.length)}
          </span>
          <span className="ac-master__sep" aria-hidden="true">
            {MIDDLE_DOT}
          </span>
        </>
      ) : null}
      <span className="ac-statline__freshness">
        {`as of ${formatClinicLocalDateTime(view.generatedAtIso, view.generatedAtZone)} (${view.generatedAtZone})`}
      </span>
      <span className="ac-master__spacer" aria-hidden="true" />
      <AcLink onClick={onRefresh} testId="workspace-refresh">
        Refresh
      </AcLink>
    </>
  );

  return (
    <section
      className="callcenter-workspace callcenter-workspace--v1 ac-page--workspace"
      data-testid="workspace-agent-v1"
      data-visual="v1"
      aria-label="My Work"
    >
      <PageHeader
        title="My Work"
        actions={phoneInDoor}
        subs={[
          // THE DECISION LINE: what needs me now, in words and figures, on
          // the ground. Decision figures first in their semantic ink; the
          // count, the freshness and the refresh door live in the master's
          // foot. A status figure is never a control.
          <span className="ac-statline" key="status" data-testid="workspace-statline">
            {figures.length === 0 ? (
              <span className="ac-statline__clear" data-testid="workspace-statline-clear">
                {NEEDS_YOU_ALL_CLEAR}
              </span>
            ) : (
              figures.map((f) => (
                <span
                  className={`ac-state ac-state--inline ac-state--${f.tone}`}
                  key={f.key}
                  data-testid={`workspace-needs-${f.key}`}
                >
                  <span className="ac-state__glyph" aria-hidden="true" />
                  <span className="ac-state__word">{`${f.count} ${f.word}`}</span>
                  {f.sub !== null ? (
                    <span className="ac-state__figure">{f.sub}</span>
                  ) : null}
                </span>
              ))
            )}
          </span>,
        ]}
        testId="workspace-header"
      />

      {view.cappedSources.length > 0 ? (
        <p className="panel-caveat" data-testid="workspace-capped">
          Large queue: some feeds are showing their newest items only
          ({view.cappedSources.join(', ')}), so counts are lower bounds.
        </p>
      ) : null}

      <MasterDetail single={!showPanel} testId="workspace-workplane">
        <Master
          ref={queueRef}
          label="My queue"
          testId="workspace-master"
          headTestId="workspace-filters-row"
          bodyTestId="workspace-master-body"
          footTestId="workspace-master-foot"
          onKeyDown={onQueueKeyDown}
          head={filtersHead}
          foot={foot}
        >
          <DataTable
            label="My queue"
            testId="workspace-queue"
            columns={columns}
            rows={visibleItems}
            rowKey={rowKeyOf}
            stickyHeader
            rowClassName={(item) =>
              rowKeyOf(item) === currentRowKey ? 'ac-table__row--selected' : undefined
            }
            rowSelected={(item) => rowKeyOf(item) === currentRowKey}
            rowTestId={(item) =>
              rowKeyOf(item) === currentRowKey ? 'workspace-row-current' : undefined
            }
            onRowClick={quickViewLawful ? selectRow : undefined}
            empty={
              view.items.length === 0 ? (
                <EmptyState
                  title="You are all caught up"
                  body="Nothing needs work right now. New work appears here the moment it arrives."
                  testId="workspace-empty"
                />
              ) : (
                <EmptyState
                  title="Nothing matches these filters"
                  body="Clear a filter to see the rest of the queue."
                  action={
                    <AcButton
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        writeFilters({ reasons: [], due: 'all', agentStaffId: null })
                      }
                    >
                      Clear filters
                    </AcButton>
                  }
                  testId="workspace-empty-filtered"
                />
              )
            }
          />
        </Master>
        {showPanel ? (
          <DetailAside label="Selected lead" testId="workspace-context">
            {selectedItem !== null && selectedItem.leadId !== null ? (
              <LeadQuickViewHost
                key={selectedItem.leadId}
                frame="panel"
                leadId={selectedItem.leadId}
                seedTitle={rowDisplayName(selectedItem)}
                onClose={() => undefined}
                onChanged={onLeadChanged}
              >
                {selectedItem.morningPriority ? (
                  // The queue's own note about this row (config-derived),
                  // off the row and into the reading.
                  <ReferenceRegion eyebrow="Queue note" testId="workspace-queue-note">
                    <p className="ac-region__text" data-testid="workspace-morning-priority">
                      {morningPriorityExplainer(view)}
                    </p>
                  </ReferenceRegion>
                ) : null}
              </LeadQuickViewHost>
            ) : (
              <Detail label="Nothing selected" testId="workspace-context-detail">
                <EmptyState
                  title="Nothing selected"
                  body="Choose a person in the queue to see their context here."
                  testId="workspace-context-empty"
                />
              </Detail>
            )}
          </DetailAside>
        ) : null}
      </MasterDetail>
    </section>
  );
}
